import React, { FormEvent, useState } from 'react';
import { InputTextField } from '@/components';
import { Button } from 'primereact/button';
import { useContainerExtractedValidations } from '@/hooks';
import classnames from 'classnames';

import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import useToastNotification from '@/hooks/useToastNotification';
import { useMutation } from '@tanstack/react-query';
import { verifyRut } from '@/pages/CustomerRegistration/verifyRut';
import { generateANPImport } from '../generateANPImport';

import styles from './ANPImport.module.scss';

export interface ANPImportFormState {
  container: string;
  dispatcherTaxId: string;
}

interface ANPImportFormErrors {
  container: string;
  dispatcherTaxId: string;
}

const ANPImport: React.FC = () => {
  const { t } = useTranslation('workOrder');
  const { showToast } = useToastNotification();

  const [formData, setFormData] = useState<ANPImportFormState>({
    container: '',
    dispatcherTaxId: '',
  });

  const [formErrors, setFormErrors] = useState<ANPImportFormErrors>({
    container: '',
    dispatcherTaxId: '',
  });

  const { marca, numero, containerIsValid } = useContainerExtractedValidations(
    formData.container
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue =
      name === 'container' ? value.replace(/\//g, '') : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: sanitizedValue,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateForm = () => {
    let errors: ANPImportFormErrors = { ...formErrors };
    let isValid = true;

    if (!formData.container || !containerIsValid) {
      errors = {
        ...errors,
        container:
          formData.container && !containerIsValid
            ? t('containerInvalid')
            : t('containerRequired'),
      };
      isValid = false;
    }
    const dispatcherTaxIdRegex = /^\d{12}$/;
    if (!formData.dispatcherTaxId) {
      errors.dispatcherTaxId = t('dispatcherTaxIdRequired');
      isValid = false;
    } else if (!dispatcherTaxIdRegex.test(formData.dispatcherTaxId)) {
      errors.dispatcherTaxId = t('dispatcherTaxIdInvalid');
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const verifyRutMutation = useMutation({
    mutationFn: verifyRut,
    onError: (error) => {
      showToast({ severity: 'error', detail: `${error}` });
    },
  });

  const generateANPImportMutation = useMutation({
    mutationFn: generateANPImport,
    onError: (error) => {
      if (error.toString().trim() !== 'Error') {
        showToast({ severity: 'error', detail: `${error}` });
      } else {
        showToast({
          severity: 'error',
          detail: t('eirFormValidations.unexpectedError'),
        });
      }
    },
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        dispatcherTaxId: formData.dispatcherTaxId,
        marca,
        numero,
      };

      try {
        const rutData = await verifyRutMutation.mutateAsync({
          rut: payload.dispatcherTaxId,
        });

        if (rutData?.m_Item1 === null && rutData?.m_Item2) {
          showToast({
            severity: 'error',
            detail: rutData?.m_Item2,
          });
        } else {
          generateANPImportMutation.mutate(payload);
        }
      } catch (error) {
        /* empty */
      }
    }
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className="col-12 flex-column md:flex-row flex align-items-start   md:align-items-center pl-0">
          <InputTextField
            className={`col-12 md:col-5 pl-0 ${styles.inputText}`}
            label={t('container')}
            id="container"
            onChange={handleInputChange}
            value={formData.container}
            name="container"
            error={formErrors.container}
            placeholder={t('containerExample')}
            longErrorMessage
          />

          <InputTextField
            className={`col-12 md:col-5 pl-0 ${styles.inputText}`}
            label={t('dispatcherTaxId')}
            id="dispatcherTaxId"
            onChange={handleInputChange}
            value={formData.dispatcherTaxId}
            name="dispatcherTaxId"
            error={formErrors.dispatcherTaxId}
            placeholder={t('dispatcherTaxIdPlaceholder')}
          />

          <Button
            label={t('getOrderButton')}
            type="submit"
            className={classnames(styles.buttonOrder)}
            disabled={
              verifyRutMutation.isPending || generateANPImportMutation.isPending
            }
            loading={
              verifyRutMutation.isPending || generateANPImportMutation.isPending
            }
          />
        </div>
      </form>
      {generateANPImportMutation.data && (
        <div
          className={classnames(
            styles.orderConfirmation,
            generateANPImportMutation.data.value.ok === false && styles.errorBg
          )}
        >
          <FontAwesomeIcon
            icon={generateANPImportMutation.data.value.ok ? faCheck : faTimes}
            size="xl"
            className={styles.iconCheck}
          />
          <div>
            <strong>{`${generateANPImportMutation.data.value.mensaje}`}</strong>
          </div>
        </div>
      )}
    </div>
  );
};
export default ANPImport;
